import React, { useContext, useState, useEffect } from "react";
import { CalculatorStepsContext } from "../../Store/CalculatorStepsContext";
import { CalculatorSteps } from "../../../model/CalculatorSteps";
import { SelectIndex } from "./SelectIndex";
import { AllocationPercentageInput } from "./AllocationPercentageInput";
import {
  VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_1,
  VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_2,
  VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_3,
  VALIDATION_MUST_CHOOSE_INDICES,
  VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_1,
  VALIDATION_MUST_ENTER_ALLOCATIONS,
  VALIDATION_NO_ALLOCATION_MAY_BE_ZERO,
  VALIDATION_ALLOCATIONS_MUST_SUM_TO_100,
  VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_2,
  VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
  getFirstValidationError,
} from "../../../model/CalculatorInputsValidation";
import { ValidationMessage } from "../ValidationMessage";
import { InvestmentIndex, allIndices } from "../../../model/CalculatorInputs";
import { pipe } from "fp-ts/lib/pipeable";
import {
  fold,
  none,
  some,
  Option,
  isSome,
  isNone,
  getOrElse,
} from "fp-ts/lib/Option";
import { CalculatorResultsContext } from "../../Store/CalculatorResultsContext";
import { fetchResults } from "../../CalculatorResults/FetchResults";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { TooltipComponent } from "../../../utils/TooltipComponent/TooltipComponent";
import { wcmUrls } from "../../../shared/constants";

const indexValidationMessages: Map<string, string> = new Map<string, string>([
  [VALIDATION_MUST_CHOOSE_INDICES, "Must select an index for all strategies"],
]);

const allocationValidationMessages = new Map<string, string>([
  [
    VALIDATION_MUST_ENTER_ALLOCATIONS,
    "Must enter a percentage allocation for all strategies",
  ],
  [
    VALIDATION_NO_ALLOCATION_MAY_BE_ZERO,
    "Cannot have a percentage allocation of 0%",
  ],
  [VALIDATION_ALLOCATIONS_MUST_SUM_TO_100, "Allocations must total 100%"],
]);

const distinct = "Strategies must be distinct.";
const distinctStrategyMessages = new Map<string, string>([
  [
    VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
    `${distinct} Strategy 1 and Strategy 2 are identical`,
  ],
  [
    VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
    `${distinct} Strategy 1 and Strategy 3 are identical`,
  ],
  [
    VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
    `${distinct} Strategy 2 and Strategy 3 are identical`,
  ],
  [
    VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    `${distinct} Strategy 1, Strategy 2, and Strategy 3 are identical`,
  ],
]);
export const Step4ChooseIndicesEnterAllocations = () => {
  const { steps, setSteps } = useContext(CalculatorStepsContext);
  const resultsStore = useContext(CalculatorResultsContext);
  const [dirty, setDirty] = useState<boolean>(false);
  const inputStepClasses = ["input-step", "input-step-4"];
  if (
    steps.stepIndex === CalculatorSteps.STEP_4_CHOOSE_INDICES_ENTER_ALLOCATIONS
  ) {
    inputStepClasses.push("current-step");
  }

  const bufferLabelId = "rila-calculator-tool-buffer-label";
  const [totalAllocation, setTotalAllocation] = useState<number>(0);

  const initialAllocationTotal = steps.inputs.strategyList.getTotalAllocation();

  useEffect(() => {
    const newTotal = steps.inputs.strategyList.getTotalAllocation();
    if (newTotal !== totalAllocation) {
      setTotalAllocation(newTotal);
    }
  }, [initialAllocationTotal]);

  useEffect(() => {
    if (steps.inputs.strategyList.getStrategyCount() === 1) {
      if (
        isNone(steps.inputs.strategyList.getAllocatedStrategy(0).allocation)
      ) {
        const oldAllStrat = steps.inputs.strategyList.getAllocatedStrategy(0);
        const onlyAllocationAt100 =
          steps.inputs.strategyList.withAllocatedStrategyAt(
            0,
            oldAllStrat.withAllocation(100)
          );
        setSteps(
          steps.withInputs(steps.inputs.withStrategyList(onlyAllocationAt100))
        );
      }
    }
  }, [steps.inputs.strategyList]);

  const handleCalculate = () => {
    const stepsWithVal = steps.validatedAtCurrentStep(steps.inputs);
    if (stepsWithVal.validation.isSuccess()) {
      // clear any validation errors before fetching results
      setSteps(
        stepsWithVal.withoutFirstValidationError().withRunValidationsOff()
      );
      resultsStore.setError(none);
      fetchResults(steps.inputs, steps.config, resultsStore);
    } else {
      const firstError = getFirstValidationError(stepsWithVal.validation);
      pipe(
        firstError,
        fold(
          () => {
            setSteps(stepsWithVal.withRunValidationsOn());
          },
          (validationError) => {
            const stepsWithFirstError = stepsWithVal
              .withFirstValidationError(validationError)
              .withRunValidationsOn();
            setSteps(stepsWithFirstError);
          }
        )
      );
      resultsStore.setLoading(false);
    }
  };

  const strategyCount = steps.inputs.strategyList.getStrategyCount();
  const strategyIndices = Array.from(Array(strategyCount).keys());

  const rowClasses = (idx: number) => {
    return [
      "index-choice-allocation-input",
      "strategy-row",
      `strategy-row-${idx + 1}`,
    ].join(" ");
  };

  function getAvailableIndices(index: number): Array<InvestmentIndex> {
    const avail = steps.config.availableInvestmentStrategies;

    const strats = steps.inputs.strategyList;
    const filteredStrategies =
      index < strats.getStrategyCount()
        ? pipe(
            steps.inputs.term,
            fold(
              () => none,
              (termLength) => {
                const allocatedStrategy = strats.getAllocatedStrategy(index);
                return pipe(
                  allocatedStrategy.strategy.strategyType,
                  fold(
                    () => none,
                    (strategyType) => {
                      return pipe(
                        allocatedStrategy.strategy.buffer,
                        fold(
                          () => none,
                          (buffer) => {
                            const strategies =
                              avail.strategiesForTermAndTypeAndBuffer(
                                termLength,
                                strategyType,
                                buffer
                              );
                            return some(strategies);
                          }
                        )
                      );
                    }
                  )
                );
              }
            )
          )
        : none;

    const enabledIndices: Array<InvestmentIndex> = pipe(
      filteredStrategies,
      fold(
        () => [],
        (strategies) => strategies.map((s) => s.index)
      )
    );
    return enabledIndices;
  }

  const indexByCode = new Map<string, InvestmentIndex>();
  for (const idx of allIndices) {
    indexByCode.set(idx.code, idx);
  }

  const selectIndexByCode = (index: number, code: string) => {
    const aStrat = steps.inputs.strategyList.getAllocatedStrategy(index);
    const invIdx = indexByCode.get(code);
    const newAllocation = invIdx
      ? aStrat.withStrategy(aStrat.strategy.withInvestmentIndex(invIdx))
      : aStrat.withStrategy(aStrat.strategy.withoutInvestmentIndex());
    const newInputs = steps.inputs.withStrategyList(
      steps.inputs.strategyList.withAllocatedStrategyAt(index, newAllocation)
    );

    setDirty(true);
    if (steps.shouldRunValidations) {
      setSteps(steps.validatedAtCurrentStep(newInputs));
    } else {
      setSteps(steps.withInputs(newInputs));
    }
  };

  function setAllocation(index: number, allocation: Option<number>) {
    const aStrat = steps.inputs.strategyList.getAllocatedStrategy(index);
    const newStrat = pipe(
      allocation,
      fold(
        () => aStrat.withoutAllocation(),
        (percentage) => aStrat.withAllocation(percentage)
      )
    );

    const newInputs = steps.inputs.withStrategyList(
      steps.inputs.strategyList.withAllocatedStrategyAt(index, newStrat)
    );

    setDirty(true);
    if (steps.shouldRunValidations) {
      setSteps(steps.validatedAtCurrentStep(newInputs));
    } else {
      setSteps(steps.withInputs(newInputs));
    }
  }

  function handleOnBlurValidation() {
    if (steps.shouldRunValidations && dirty) {
      setSteps(steps.validatedAtCurrentStep(steps.inputs));
    }
  }

  const allocationTotalClasses = steps.validation.hasIssue(
    VALIDATION_ALLOCATIONS_MUST_SUM_TO_100
  )
    ? "allocation-total validation-error"
    : "allocation-total";

  const specificIndexIssues = [
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_1,
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_2,
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_3,
  ];
  const specificAllocationIssues = [
    VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_1,
    VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_2,
    VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_3,
  ];

  const loadingIcon = (
    <FontAwesomeIcon
      data-testid="fa-loading-spinner-icon"
      icon={faSpinner}
      size="1x"
      color="white"
      spin
    />
  );

  const loadingSpinner = resultsStore.loading ? (
    <span className="icon">{loadingIcon}</span>
  ) : null;

  const indexValidationMsgId = "rila-calculator-index-validation-message";
  const allocationValidationMsgId =
    "rila-calculator-allocation-validation-message";
  const uniqueStrategyValidationMsgId =
    "rila-calculator-unique-strategy-validation-message";

  const allIssues = [
    [
      VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_1,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
    [
      VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
    ],
    [VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_3],
  ];

  const issues = [
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_1,
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_2,
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_3,
  ];

  function indexValidationIssue(idx: number): string | undefined {
    const uniquenessIssues = [
      [
        VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
        VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
        VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
      ],
      [
        VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
        VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
        VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
      ],
      [
        VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
        VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
        VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
      ],
    ];

    const indexNotSelected = steps.validation.hasIssue(
      specificIndexIssues[idx]
    );
    const strategyNotUnique = steps.validation.hasIssueIn(
      uniquenessIssues[idx]
    );
    if (indexNotSelected) {
      return indexValidationMsgId;
    } else if (strategyNotUnique) {
      return uniqueStrategyValidationMsgId;
    } else {
      return undefined;
    }
  }

  const duplicateIssues = [
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
  ];

  function hasAllocationIssue(idx: number): boolean {
    return (
      steps.validation.hasIssue(specificAllocationIssues[idx]) ||
      steps.validation.hasIssue(VALIDATION_ALLOCATIONS_MUST_SUM_TO_100) ||
      getOrElse(() => -1)(
        steps.inputs.strategyList.getAllocatedStrategy(idx).allocation
      ) === 0
    );
  }

  return (
    <div id="input-section-4" className={inputStepClasses.join(" ")}>
      <div className="input-fields">
        <div className="step-header index-allocations">
          <div className="label-header">
            <h3 id={bufferLabelId}>
              Index{" "}
              <TooltipComponent
                title="Indices"
                url={wcmUrls.indicesTooltip}
                screenreaderText="More info on Index"
              />
            </h3>
          </div>
          <div className="description">
            <p className="instruction">
              Choose an index option, and a percentage of the premium to
              allocate.
            </p>
            <p className="note">
              Some index options are not available
              with&nbsp;all&nbsp;index&nbsp;crediting&nbsp;strategies.
            </p>
          </div>
        </div>
        <div className="inputs step-4-inputs">
          {strategyIndices.map((i) => (
            <div key={"row-" + i} className={rowClasses(i)}>
              <SelectIndex
                key={"idx" + i}
                index={i}
                investmentIndex={
                  steps.inputs.strategyList.getAllocatedStrategy(i).strategy
                    .investmentIndex
                }
                availableInvestmentIndices={getAvailableIndices(i)}
                invalid={steps.validation.hasIssue(issues[i])}
                isDuplicateStrategy={steps.validation.hasIssueIn(
                  duplicateIssues[i]
                )}
                selectInvestmentIndex={(code: string) =>
                  selectIndexByCode(i, code)
                }
                validationIssues={allIssues[i]}
                validationMessageId={indexValidationIssue(i)}
              />
              <AllocationPercentageInput
                key={"pct-" + i}
                index={i}
                validationIssue={specificAllocationIssues[i]}
                invalid={hasAllocationIssue(i)}
                showInput={isSome(
                  steps.inputs.strategyList.getAllocatedStrategy(i).strategy
                    .strategyType
                )}
                allocation={
                  steps.inputs.strategyList.getAllocatedStrategy(i).allocation
                }
                setAllocation={(percentage: number) =>
                  setAllocation(i, some(percentage))
                }
                unsetAllocation={() => setAllocation(i, none)}
                onBlur={handleOnBlurValidation}
                validationMessageId={allocationValidationMsgId}
              />
            </div>
          ))}
          <div className={allocationTotalClasses}>
            <strong>Total:</strong> {totalAllocation}%
          </div>
        </div>
        <div className="validation-messages">
          <ValidationMessage
            id={uniqueStrategyValidationMsgId}
            messagesByIssue={distinctStrategyMessages}
          />
          <ValidationMessage
            id={indexValidationMsgId}
            messagesByIssue={indexValidationMessages}
          />
          <ValidationMessage
            id={allocationValidationMsgId}
            messagesByIssue={allocationValidationMessages}
          />
        </div>
      </div>
      <div className="next-button-row">
        <button
          type="button"
          className="btn btn-next"
          onClick={handleCalculate}
          disabled={resultsStore.loading}
        >
          Calculate {loadingSpinner}
        </button>
      </div>
    </div>
  );
};
